import { DEFAULT_THEME_ACCENT, normalizeAccentColor } from "@/lib/theme/constants";

export { DEFAULT_THEME_ACCENT, normalizeAccentColor };

export type ThemeAccentPreset = {
  id: string;
  label: string;
  color: string;
};

export const THEME_ACCENT_PRESETS: ThemeAccentPreset[] = [
  { id: "blue", label: "Blue", color: "#2563eb" },
  { id: "sky", label: "Sky", color: "#0ea5e9" },
  { id: "teal", label: "Teal", color: "#0d9488" },
  { id: "green", label: "Green", color: "#059669" },
  { id: "indigo", label: "Indigo", color: "#4f46e5" },
  { id: "violet", label: "Violet", color: "#7c3aed" },
  { id: "rose", label: "Rose", color: "#e11d48" },
  { id: "orange", label: "Orange", color: "#ea580c" },
  { id: "slate", label: "Slate", color: "#475569" },
];

function hexToRgb(hex: string) {
  const normalized = hex.replace("#", "");
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number) {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`;
}

export function darkenHex(hex: string, amount = 0.14): string {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
}

function mixHex(hex: string, target: string, accentWeight: number): string {
  const accent = hexToRgb(hex);
  const base = hexToRgb(target);
  const mix = (a: number, b: number) => Math.round(a * accentWeight + b * (1 - accentWeight));
  return rgbToHex(mix(accent.r, base.r), mix(accent.g, base.g), mix(accent.b, base.b));
}

export function lightenHex(hex: string, amount = 0.28): string {
  return mixHex(hex, "#ffffff", 1 - amount);
}

function linearChannel(value: number): number {
  const channel = value / 255;
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  return 0.2126 * linearChannel(r) + 0.7152 * linearChannel(g) + 0.0722 * linearChannel(b);
}

function contrastRatio(first: string, second: string): number {
  const lighter = Math.max(relativeLuminance(first), relativeLuminance(second));
  const darker = Math.min(relativeLuminance(first), relativeLuminance(second));
  return (lighter + 0.05) / (darker + 0.05);
}

function ensureContrast(hex: string, background: string, mixTarget: string, minimum = 4.5): string {
  if (contrastRatio(hex, background) >= minimum) return hex;
  for (let targetWeight = 0.08; targetWeight <= 0.88; targetWeight += 0.08) {
    const candidate = mixHex(hex, mixTarget, 1 - targetWeight);
    if (contrastRatio(candidate, background) >= minimum) return candidate;
  }
  return mixTarget;
}

export function readableForeground(hex: string): "#0f172a" | "#ffffff" {
  return contrastRatio(hex, "#0f172a") >= contrastRatio(hex, "#ffffff") ? "#0f172a" : "#ffffff";
}

export type SidebarPalette = {
  bg: string;
  border: string;
  fg: string;
  muted: string;
  hoverBg: string;
  activeBg: string;
  activeFg: string;
  badgeBg: string;
  logoBg: string;
};

export function deriveSidebarPalette(accent: string): SidebarPalette {
  return {
    bg: mixHex(accent, "#0f172a", 0.36),
    border: "rgba(255, 255, 255, 0.12)",
    fg: "#ffffff",
    muted: "#ffffff",
    hoverBg: "rgba(255, 255, 255, 0.08)",
    activeBg: "rgba(255, 255, 255, 0.14)",
    activeFg: "#ffffff",
    badgeBg: "rgba(255, 255, 255, 0.16)",
    logoBg: "rgba(255, 255, 255, 0.14)",
  };
}

const SIDEBAR_CSS_VARS = [
  "--user-sidebar-bg",
  "--user-sidebar-border",
  "--user-sidebar-fg",
  "--user-sidebar-muted",
  "--user-sidebar-hover-bg",
  "--user-sidebar-active-bg",
  "--user-sidebar-active-fg",
  "--user-sidebar-badge-bg",
  "--user-sidebar-logo-bg",
] as const;

const ACCENT_CSS_VARS = [
  "--user-accent-original",
  "--user-accent",
  "--user-accent-deep",
  "--user-accent-dark",
  "--user-accent-foreground",
  "--user-accent-dark-foreground",
] as const;

function applySidebarPalette(root: HTMLElement, palette: SidebarPalette) {
  root.style.setProperty("--user-sidebar-bg", palette.bg);
  root.style.setProperty("--user-sidebar-border", palette.border);
  root.style.setProperty("--user-sidebar-fg", palette.fg);
  root.style.setProperty("--user-sidebar-muted", palette.muted);
  root.style.setProperty("--user-sidebar-hover-bg", palette.hoverBg);
  root.style.setProperty("--user-sidebar-active-bg", palette.activeBg);
  root.style.setProperty("--user-sidebar-active-fg", palette.activeFg);
  root.style.setProperty("--user-sidebar-badge-bg", palette.badgeBg);
  root.style.setProperty("--user-sidebar-logo-bg", palette.logoBg);
  root.classList.add("accent-sidebar");
}

function clearSidebarPalette(root: HTMLElement) {
  for (const key of SIDEBAR_CSS_VARS) {
    root.style.removeProperty(key);
  }
  root.classList.remove("accent-sidebar");
}

export function applyUserAccent(hex: string) {
  const normalized = normalizeAccentColor(hex);
  if (!normalized) return;
  const root = document.documentElement;
  const lightAccent = ensureContrast(normalized, "#ffffff", "#0f172a");
  const darkAccent = ensureContrast(lightenHex(normalized), "#0b1220", "#ffffff");
  root.style.setProperty("--user-accent-original", normalized);
  root.style.setProperty("--user-accent", lightAccent);
  root.style.setProperty("--user-accent-deep", darkenHex(lightAccent));
  root.style.setProperty("--user-accent-dark", darkAccent);
  root.style.setProperty("--user-accent-foreground", readableForeground(lightAccent));
  root.style.setProperty("--user-accent-dark-foreground", readableForeground(darkAccent));
  applySidebarPalette(root, deriveSidebarPalette(normalized));
}

export function clearUserAccent() {
  const root = document.documentElement;
  for (const key of ACCENT_CSS_VARS) {
    root.style.removeProperty(key);
  }
  clearSidebarPalette(root);
}
